import { cn } from '@dfh-corp-sites/ui/utils'
import React, { FC, useMemo } from 'react'

import {
  SanityOrMagentoProductImage,
  SanityProductImageSelection,
  SingleSourceOrSourceSet,
} from '../../../../lib/products/images'
import { imageUrlFor } from '../../../../utils/image-url'
import ImageWithMagnifier from './ImageWithMagnifier'
import { ItemImagesProps } from './ItemImages'

export interface ItemImageSourceFactory {
  (image: SanityOrMagentoProductImage): {
    source: SingleSourceOrSourceSet
    srcZoom: string
    altText: string
  }
}

export interface ItemImageProps {
  className?: string
  /**
   * Support Magento and Sanity image types for now - prefer Sanity and use image transformation API
   */
  image: SanityOrMagentoProductImage
  imageSourceFactory: ItemImageSourceFactory | undefined
  showItemImagePreviews: boolean
  size: ItemImagesProps['size']
}

function isSanityProductImage(
  image: SanityOrMagentoProductImage,
): image is SanityProductImageSelection {
  return 'asset' in image && image.asset !== undefined
}

/**
 * Default image source factory for product details page
 */
const sourceForImage: ItemImageSourceFactory = (image) => {
  if (!isSanityProductImage(image)) {
    return {
      source: { _tag: 'SingleSource' as const, src: image.url },
      srcZoom: image.url,
      altText: image.label || '',
    }
  }

  const imageUrlForWidth = (width: number) =>
    imageUrlFor(image.asset).width(width).auto('format').url()

  const defaultSrc = imageUrlForWidth(800) ?? ''
  const srcSet = [
    `${imageUrlForWidth(400)} 400w`,
    `${imageUrlForWidth(600)} 600w`,
    `${imageUrlForWidth(800)} 800w`,
    `${imageUrlForWidth(1000)} 1000w`,
    `${imageUrlForWidth(1400)} 1400w`,
    `${imageUrlForWidth(1600)} 1600w`,
  ].join(', ')
  const sizes = ['(max-width: 599px) 95vw', '(max-width: 1055px) 45vw', '475px'].join(', ')
  return {
    source: { _tag: 'SourceSet' as const, defaultSrc, srcSet, sizes },
    srcZoom: imageUrlForWidth(2000) ?? '',
    altText: image.alt || '',
  }
}

const ItemImage: FC<ItemImageProps> = ({
  className,
  image,
  imageSourceFactory = sourceForImage,
  showItemImagePreviews,
  size,
}) => {
  const { source, srcZoom, altText } = useMemo(
    () => imageSourceFactory(image),
    [image, imageSourceFactory],
  )
  return (
    <div
      className={cn(
        'flex-1 bg-transparent pt-0 pb-2 px-4 text-center w-full md:h-auto md:ml-auto md:max-h-[475px] md:max-w-[475px] md:p-0 md:pb-2',
        showItemImagePreviews ? 'md:mr-0' : 'md:mr-auto',
        size === 'lg' && 'md:max-w-full md:max-h-full',
        className,
      )}
    >
      <ImageWithMagnifier
        source={source}
        srcZoom={srcZoom}
        alt={altText}
        magnifierWidth={450}
        magnifierHeight={350}
        zoomFactor={0.6}
      />
    </div>
  )
}

export default ItemImage
